import { ParsedLog, SectionHeader } from './types'

function trimmedSlice(
  source: string,
  start: number,
  end: number,
): { content: string, sourceStart: number, sourceEnd: number } | null {
  const raw = source.slice(start, end)
  const content = raw.trim()
  if (!content)
    return null
  const leadingLength = raw.length - raw.trimStart().length
  const trailingLength = raw.length - raw.trimEnd().length
  return {
    content,
    sourceStart: start + leadingLength,
    sourceEnd: end - trailingLength,
  }
}

export function parsePlayerLog(logContent: string): ParsedLog[] {
  if (!logContent) {
    return []
  }

  const parts: ParsedLog[] = []

  const quickViewMarkerRegex = /^## ———💬[^\n]*———$/gm
  const markers: Array<{ index: number, length: number }> = []
  let markerMatch: RegExpExecArray | null
  while ((markerMatch = quickViewMarkerRegex.exec(logContent)) !== null) {
    markers.push({ index: markerMatch.index, length: markerMatch[0].length })
  }

  let contentToParse = logContent
  let contentBaseOffset = 0
  let setupPart: ReturnType<typeof trimmedSlice> = null

  if (markers.length >= 2) {
    setupPart = trimmedSlice(logContent, 0, markers[0].index)
    contentBaseOffset = markers[1].index + markers[1].length
    contentToParse = logContent.slice(contentBaseOffset)
  }

  const roundHeaderRegex = /^### [^\n]*?🌟[^\n]*?(\d+)[^\n]*/gm

  const headers: SectionHeader[] = []

  let match: RegExpExecArray | null
  while ((match = roundHeaderRegex.exec(contentToParse)) !== null) {
    headers.push({
      type: 'round',
      index: match.index,
      length: match[0].length,
      roundNum: parseInt(match[1], 10),
    })
  }

  headers.sort((a, b) => a.index - b.index)

  if (headers.length === 0) {
    const finalSetup = setupPart
      ?? trimmedSlice(
        logContent,
        contentBaseOffset,
        contentBaseOffset + contentToParse.length,
      )
    if (finalSetup)
      parts.push({ round: 0, ...finalSetup })
    return parts
  }

  const firstHeader = headers[0]
  if (setupPart) {
    parts.push({ round: 0, ...setupPart })
  }
  else if (firstHeader.index > 0) {
    const extractedSetup = trimmedSlice(
      logContent,
      contentBaseOffset,
      contentBaseOffset + firstHeader.index,
    )
    if (extractedSetup)
      parts.push({ round: 0, ...extractedSetup })
  }

  for (let i = 0; i < headers.length; i++) {
    const header = headers[i]
    const nextHeader = headers[i + 1]

    const startContentIdx = header.index + header.length
    const endContentIdx = nextHeader ? nextHeader.index : contentToParse.length

    const content = trimmedSlice(
      logContent,
      contentBaseOffset + startContentIdx,
      contentBaseOffset + endContentIdx,
    )

    if (content)
      parts.push({ round: header.roundNum, ...content })
  }
  return parts.sort((a, b) => a.round - b.round)
}
